import React from 'react';
import { cn } from '../utils';
import { FormControl } from './FormControl';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: 'bordered' | 'ghost' | 'default';
  inputSize?: 'xs' | 'sm' | 'md' | 'lg';
  state?: 'default' | 'success' | 'warning' | 'error';
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftAddon?: React.ReactNode;
  rightAddon?: React.ReactNode;
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'bordered',
      inputSize = 'md',
      state = 'default',
      fullWidth = true,
      leftIcon,
      rightIcon,
      leftAddon,
      rightAddon,
      label,
      error,
      helperText,
      className,
      required,
      ...props
    },
    ref
  ) => {
    // Override state if error is provided
    const effectiveState = error ? 'error' : state;

    // Update input classes with effective state
    const finalInputClasses = cn(
      'input',
      variant === 'bordered' && 'input-bordered',
      variant === 'ghost' && 'input-ghost',
      inputSize === 'xs' && 'input-xs',
      inputSize === 'sm' && 'input-sm',
      inputSize === 'lg' && 'input-lg',
      effectiveState === 'success' && 'input-success',
      effectiveState === 'warning' && 'input-warning',
      effectiveState === 'error' && 'input-error',
      fullWidth && 'w-full',
      (leftIcon || leftAddon) && 'pl-10',
      (rightIcon || rightAddon) && 'pr-10',
      className
    );

    const inputElement =
      leftIcon || rightIcon || leftAddon || rightAddon ? (
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 z-10">
              {leftIcon}
            </span>
          )}
          {leftAddon && (
            <span className="absolute left-0 top-0 bottom-0 flex items-center px-3 bg-base-200 border-r border-base-300 rounded-l-lg">
              {leftAddon}
            </span>
          )}

          <input ref={ref} className={finalInputClasses} {...props} />

          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 z-10">
              {rightIcon}
            </span>
          )}
          {rightAddon && (
            <span className="absolute right-0 top-0 bottom-0 flex items-center px-3 bg-base-200 border-l border-base-300 rounded-r-lg">
              {rightAddon}
            </span>
          )}
        </div>
      ) : (
        <input ref={ref} className={finalInputClasses} {...props} />
      );

    // If label or error/helperText is provided, wrap in FormControl
    if (label || error || helperText) {
      const formControlProps: any = {
        className: fullWidth ? 'w-full' : undefined,
      };

      if (label) formControlProps.label = label;
      if (error) formControlProps.error = error;
      if (helperText && !error) formControlProps.helperText = helperText;
      if (required) formControlProps.required = required;

      return <FormControl {...formControlProps}>{inputElement}</FormControl>;
    }

    return inputElement;
  }
);

Input.displayName = 'Input';
